import { useQuery } from "@tanstack/react-query";
import { Inbox, MailQuestion, Settings2 } from "lucide-react";

import {
  SUPPORT_VIEW_KEYS,
  type SupportViewCounts,
  type SupportViewKey,
} from "../../shared/support-schemas";
import { cn } from "../lib/utils";
import type { OperationsNavigate } from "./navigation";
import { OperationsLink } from "./OperationsLink";
import { PAGE_WIDTH } from "./page-width";
import { getSupportViews, LIVE_LIST, supportKeys } from "./support-api";
import { VIEW_LABELS } from "./support-format";
import { parseSupportRoute, supportViewPath, type SupportRoute } from "./support-route";
import { SupportSettings } from "./SupportSettings";
import { SupportTicketPage } from "./SupportTicketPage";
import { SupportTicketQueue } from "./SupportTicketQueue";
import { SupportUnmatched } from "./SupportUnmatched";

/**
 * The support desk (ADR-0089), laid out like a helpdesk agent workspace: views with counts on the
 * left and the selected view's queue beside them. A ticket opens as its own page.
 */
export function SupportDesk({
  path,
  navigate,
  canWrite,
  canManage = false,
}: {
  path: string;
  navigate: OperationsNavigate;
  canWrite: boolean;
  /** Holds `support.automation.manage` for every customer, so may change the AI settings. */
  canManage?: boolean;
}) {
  const parsed = parseSupportRoute(path);
  const route: SupportRoute =
    parsed === null || (parsed.kind === "settings" && !canManage)
      ? { kind: "view", view: "open" }
      : parsed;
  if (route.kind === "ticket")
    return <SupportTicketPage ticketId={route.ticketId} navigate={navigate} canWrite={canWrite} />;
  return (
    <div
      id="operations-support"
      className={cn(
        "mx-auto grid w-full gap-6 p-6 sm:p-8 lg:grid-cols-[15rem_minmax(0,1fr)]",
        PAGE_WIDTH,
      )}
    >
      <SupportViewNav route={route} navigate={navigate} canManage={canManage} />
      <div className="min-w-0">
        {route.kind === "unmatched" ? (
          <SupportUnmatched canWrite={canWrite} navigate={navigate} />
        ) : route.kind === "settings" ? (
          <SupportSettings navigate={navigate} />
        ) : (
          <SupportTicketQueue
            key={route.view}
            view={route.view}
            navigate={navigate}
            canWrite={canWrite}
          />
        )}
      </div>
    </div>
  );
}

function SupportViewNav({
  route,
  navigate,
  canManage,
}: {
  route: SupportRoute;
  navigate: OperationsNavigate;
  canManage: boolean;
}) {
  const counts = useQuery({
    queryKey: supportKeys.views(),
    queryFn: getSupportViews,
    ...LIVE_LIST,
  });
  return (
    <nav aria-label="Support views" className="flex flex-col gap-1">
      <h1 className="mb-2 text-2xl font-extrabold">Support</h1>
      {SUPPORT_VIEW_KEYS.map((key) => (
        <ViewLink
          key={key}
          href={supportViewPath(key)}
          label={VIEW_LABELS[key]}
          count={countFor(counts.data, key)}
          active={route.kind === "view" && route.view === key}
          navigate={navigate}
        />
      ))}
      {counts.data?.unmatched === null || counts.data === undefined ? null : (
        <>
          <div className="my-2 h-px bg-muted" />
          <ViewLink
            href="/support/unmatched"
            label="Unmatched email"
            count={counts.data.unmatched}
            active={route.kind === "unmatched"}
            navigate={navigate}
            icon={MailQuestion}
          />
        </>
      )}
      {canManage ? (
        <>
          <div className="my-2 h-px bg-muted" />
          <ViewLink
            href="/support/settings"
            label="Settings"
            count={null}
            active={route.kind === "settings"}
            navigate={navigate}
            icon={Settings2}
          />
        </>
      ) : null}
      {counts.error ? (
        <p className="mt-2 text-sm text-destructive">{counts.error.message}</p>
      ) : null}
    </nav>
  );
}

const countFor = (counts: SupportViewCounts | undefined, key: SupportViewKey) =>
  counts?.views.find((view) => view.key === key)?.count ?? null;

function ViewLink({
  href,
  label,
  count,
  active,
  navigate,
  icon: Icon = Inbox,
}: {
  href: string;
  label: string;
  count: number | null;
  active: boolean;
  navigate: OperationsNavigate;
  icon?: typeof Inbox;
}) {
  return (
    <OperationsLink
      href={href}
      navigate={navigate}
      ariaCurrent={active ? "page" : undefined}
      className={cn(
        "flex min-h-10 items-center gap-2 rounded-md px-3 text-base font-medium hover:bg-muted",
        active && "bg-info-background font-semibold text-link",
      )}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count === null ? null : (
        <span className="text-sm text-muted-foreground tabular-nums">{count}</span>
      )}
    </OperationsLink>
  );
}
